import { StateStatus } from "shared/enums/StateStatus";
import { ApiProgramTimelineItemForRegDashboard } from "shared/types/ApiEventResponseWithProgramTimeline";
import { Copies } from "shared/hooks/useCopies";
import Icon from "../../../../assets/icons";
import { ProgramMenuKey } from "./enum";
import { ConsoleRouteMap } from "../../routes/routes";
import Authority from "shared/enums/Authority";
import { ConfigurationNavKey } from "./configuration/enum";
import React from "react";
import { isMeetupProgram, isTabletalkProgram } from "shared/utils";
import { NavOptionType } from "../../components/navigation/context/type";
import { getFslNavChildOption } from "./configuration/selection/util";
import { ApiProgramResponse } from "../../../../shared/types/ApiProgramResponse";
import getProgramTypeIcon from "../../../../shared/utils/getProgramType";
import { ProgramOrganizationsNavMenuIsAvailable } from "../../../../shared/enums/ProgramType";

export function hasProgramStateElapsed(state: string, timeline: Array<ApiProgramTimelineItemForRegDashboard>) {
  return timeline.some(item => item.state === state && item.stateStatus === StateStatus.ELAPSED);
}

function getTabletalkNavigationOptions({ copies }: { copies: Copies }): Array<NavOptionType> {
  return [
    {
      key: ConfigurationNavKey.TABLETALK_TOPIC,
      icon: <Icon.Folder />,
      displayLabel: copies.get("tabletalk.topic"),
      path: ConsoleRouteMap.PROGRAM.CONFIGURATIONS.TABLETALK_TOPIC.path,
      requiredAuthorities: [Authority.PROGRAM_READ]
    }
  ];
}

function getMeetupNavigationOptions({ copies }: { copies: Copies }): Array<NavOptionType> {
  return [
    {
      key: ConfigurationNavKey.MEETUP_PROFILE,
      icon: <Icon.ProfileSquare />,
      displayLabel: copies.get("profile"),
      path: ConsoleRouteMap.PROGRAM.CONFIGURATIONS.MEETUP_PROFILE.path,
      requiredAuthorities: [Authority.PROGRAM_READ]
    }
  ];
}

export function getProgramConfigurationNavItem({
  copies,
  programDetails
}: {
  copies: Copies;
  programDetails: ApiProgramResponse;
}) {
  let basicProgramSpecificNavOptions: Array<NavOptionType> = [];
  if (isMeetupProgram(programDetails.programType)) {
    basicProgramSpecificNavOptions = getMeetupNavigationOptions({ copies });
  }
  if (isTabletalkProgram(programDetails.programType)) {
    basicProgramSpecificNavOptions = getTabletalkNavigationOptions({ copies });
  }
  const navItemObj = {
    icon: <Icon.Settings />,
    key: ProgramMenuKey.CONFIGURATION,
    displayLabel: copies.get("configuration"),
    path: ConsoleRouteMap.PROGRAM.CONFIGURATIONS.path,
    requiredAuthorities: [Authority.PROGRAM_READ],
    isCollapsable: true,
    child: [
      {
        key: ConfigurationNavKey.PROGRAM_CONFIG_HEADING,
        displayLabel: copies.get("program.config.nav.menu.program.config.group.heading"),
        path: ConsoleRouteMap.PROGRAM.CONFIGURATIONS.path,
        requiredAuthorities: [Authority.PROGRAM_READ],
        child: [
          {
            key: ConfigurationNavKey.UPDATE_PROGRAM,
            icon: getProgramTypeIcon({
              programType: programDetails.programType,
              programIconImage: programDetails.icon
            }),
            displayLabel: copies.get("program"),
            path: ConsoleRouteMap.PROGRAM.CONFIGURATIONS.UPDATE_PROGRAM.path,
            requiredAuthorities: [Authority.PROGRAM_READ]
          },
          {
            key: ConfigurationNavKey.TIMELINE_CONFIGURATION,
            icon: <Icon.TimeLineBar />,
            displayLabel: copies.get("timeline"),
            path: ConsoleRouteMap.PROGRAM.CONFIGURATIONS.TIMELINES.path,
            requiredAuthorities: [Authority.PROGRAM_READ]
          },
          ...basicProgramSpecificNavOptions
        ]
      }
    ]
  };

  if (isMeetupProgram(programDetails.programType)) {
    navItemObj.child.push({
      key: ConfigurationNavKey.PROGRAM_SELECTION_CONFIG_HEADING,
      displayLabel: copies.get("program.config.nav.menu.program.selection.config.group.heading"),
      path: ConsoleRouteMap.PROGRAM.CONFIGURATIONS.path,
      requiredAuthorities: [Authority.PROGRAM_READ],
      child: [
        {
          key: ConfigurationNavKey.SELECTION_CARD_CONFIGURATION,
          displayLabel: copies.get("card"),
          icon: <Icon.RequestCard />,
          path: ConsoleRouteMap.PROGRAM.CONFIGURATIONS.cardConfig.path,
          requiredAuthorities: [Authority.PROGRAM_READ]
        },
        ...getFslNavChildOption({ copies, programType: programDetails.programType })
      ]
    });
  }

  return navItemObj;
}

export function getProgramNavOptions({
  programDetails,
  copies
}: {
  programDetails: ApiProgramResponse;
  copies: Copies;
}) {
  const options: NavOptionType = {
    key: ProgramMenuKey.PROGRAM_HOME,
    displayLabel: "",
    path: ConsoleRouteMap.PROGRAM.path,
    backLinkLabel: copies.get("back.to.program"),
    child: [
      {
        icon: <Icon.Dashboard />,
        key: ProgramMenuKey.WORKFLOW,
        displayLabel: copies.get("workflow"),
        path: ConsoleRouteMap.PROGRAM.WORKFLOW.path,
        requiredAuthorities: [Authority.PROGRAM_READ]
      }
    ]
  };

  if (ProgramOrganizationsNavMenuIsAvailable.includes(programDetails.programType)) {
    options.child?.push({
      icon: <Icon.Organization />,
      key: ProgramMenuKey.PROGRAM_ORGANIZATION,
      displayLabel: copies.get("program.organizations"),
      path: ConsoleRouteMap.PROGRAM.ORGANIZATION.path,
      requiredAuthorities: [Authority.PROGRAM_READ]
    });
  }

  const programConfigNavItem: NavOptionType = getProgramConfigurationNavItem({
    copies,
    programDetails
  });

  options?.child?.push(programConfigNavItem);
  return options;
}
